import {
  Action,
  DropdownActionMenuItem,
  openDeleteDialog,
  openDialog,
  showNotification,
} from 'platform/components';

import {
  useDeleteWorkshopInterestMutation,
  useGetWorkshopInterestQuery,
  useOpenWorkshopInterestMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {testIds, workshopRoutes} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {buildArray, useNavigate} from 'shared/web';

import {WorkshopInterestEndCaseDialog} from '../components/WorkshopInterestEndCaseDialog';

export function useWorkshopInterestActions(interestId: string) {
  const navigate = useNavigate();

  const {data: workshopInterest} = useGetWorkshopInterestQuery({id: interestId});

  const [deleteWorkshopInterest] = useDeleteWorkshopInterestMutation();
  const [openWorkshopInterest, {isLoading: isOpenLoading}] = useOpenWorkshopInterestMutation();

  const canDelete = workshopInterest?.availableActions?.includes('DELETE') ?? false;
  const canOpen = workshopInterest?.availableActions?.includes('OPEN') ?? false;
  const canEnd = workshopInterest?.availableActions?.includes('END') ?? false;

  const onEndClick = () =>
    openDialog(
      <WorkshopInterestEndCaseDialog
        data-testid={testIds.workshop.interestDetail('endCaseDialog')}
        interestId={interestId}
      />,
      {
        'data-testid': testIds.workshop.interestDetail('endCaseDialog'),
        title: i18n.t('entity.workshopInterest.labels.endInterestCase'),
        scrollBehavior: 'outside',
      }
    );

  const dropdownMenuItems = buildArray<DropdownActionMenuItem>()
    .when(canEnd, {
      label: i18n.t('general.actions.end'),
      onClick: onEndClick,
    })
    .when(canDelete, {
      label: i18n.t('general.actions.delete'),
      onClick: () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteWorkshopInterest({id: interestId})
              .unwrap()
              .then(() => {
                showNotification.success(i18n.t('general.notifications.successfullyDeleted'));
                navigate(workshopRoutes.interest);
              })
              .catch(handleApiError),
        }),
    });

  return buildArray<Action>()
    .when(
      (workshopInterest?.state === 'NEW' || workshopInterest?.state === 'OPEN') &&
        dropdownMenuItems.length > 0,
      {
        type: 'dropdown-iconButton',
        priority: 'secondary',
        menuItems: dropdownMenuItems,
        'data-testid': testIds.workshop.interestDetail('headerActions-submenu'),
      }
    )
    .when(workshopInterest?.state === 'OPEN' && canEnd, {
      type: 'button',
      variant: 'secondary',
      title: i18n.t('general.actions.end'),
      onClick: onEndClick,
      'data-testid': testIds.workshop.interestDetail('headerActions-end'),
    })
    .when(workshopInterest?.state === 'NEW' && canOpen, {
      type: 'button',
      variant: 'primary',
      title: i18n.t('general.actions.open'),
      isLoading: isOpenLoading,
      onClick: () => openWorkshopInterest({id: interestId}).unwrap().catch(handleApiError),
      'data-testid': testIds.workshop.interestDetail('headerActions-open'),
    });
}
